import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import AppSidebar from './AppSidebar';
import { Navbar } from './common/Catalyst/navbar';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import { Heading } from './common/Catalyst/heading';
import { Divider } from './common/Catalyst/divider';
import { Button } from './common/Catalyst/button';
import { Dialog, DialogBody, DialogTitle } from './common/Catalyst/dialog';
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
  ErrorMessage,
} from './common/Catalyst/fieldset';
import { Input } from './common/Catalyst/input';
import { vendorService } from './clients/ApiClients';
import { VendorReturn, VendorUpdate } from './clients/rust-api-client';
import { formatDate } from './utils/dateUtils';
import { canDeleteVendor } from './utils/TokenUtils';
import { FieldPicker } from './common/Custom/FieldPicker';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Textarea } from './common/Catalyst/textarea';
import ShowAlert from './ShowAlert';
import { ErrorPage } from './common/Custom/ErrorPage';
import LoadingSpinner from './LoadingSpinner';
import { fresher, useSyncedVendor } from './sync/hooks';
import { apiErrorMessage } from './utils/apiErrors';
import { CreateActions } from './shell/CreateActions';
import VendorBankDetailsCard from './VendorBankDetailsCard';

interface TouchedFields {
  [key: string]: boolean;
}

interface FormErrors {
  [key: string]: string;
}

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const VendorDetailPage: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ownVendor, setVendor] = useState<VendorReturn | null>(null);
  // The sync engine's local copy when it has this vendor (#561), or what
  // this page last got back from the server if that is newer.
  const syncedVendor = useSyncedVendor(vendorId);
  const vendor = fresher(syncedVendor, ownVendor);
  const haveSynced = !!syncedVendor;
  const [showSaveError, setShowSaveError] = useState(false);
  const [saveErrorTitle, setSaveErrorTitle] = useState<string>('');
  const [saveError, setSaveError] = useState<string>('');

  const [showFetchError, setShowFetchError] = useState(false);
  const [fetchErrorTitle, setFetchErrorTitle] = useState<string>('');
  const [fetchError, setFetchError] = useState<string>('');
  // Bumped by the error page's "Try again" to run the load again.
  const [reloadKey, setReloadKey] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editedVendor, setEditedVendor] = useState<VendorUpdate | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [touchedFields, setTouchedFields] = useState<TouchedFields>({});
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const fetchVendor = async () => {
      if (haveSynced) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await vendorService.getVendorById({
          vendorId: vendorId!,
        });
        setVendor(response.data);
        setShowFetchError(false);
      } catch (err) {
        console.error('Error fetching vendor:', err);
        const errorMessage = apiErrorMessage(err, "Couldn't load the vendor.");

        setFetchErrorTitle("Couldn't load vendor details");
        setFetchError(errorMessage);
        setShowFetchError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchVendor();
  }, [vendorId, haveSynced, reloadKey]);

  const handleEdit = () => {
    if (vendor) {
      const {
        id,
        date_created,
        date_updated,
        hoa_id,
        created_by,
        updated_by,
        ...editableFields
      } = vendor;
      setEditedVendor(editableFields as VendorUpdate);
      setIsEditing(true);
      setTouchedFields({});
      setFormErrors({});
    }
  };

  const handleSave = async () => {
    if (!editedVendor || !vendor) return;

    setIsSaving(true);
    setShowSaveError(false);
    setShowSuccessAlert(false);

    const updatePayload: VendorUpdate = {};

    // Only include fields that have changed
    if (editedVendor.name !== vendor.name) {
      updatePayload.name = editedVendor.name;
    }
    if (editedVendor.contact_name !== vendor.contact_name) {
      updatePayload.contact_name = editedVendor.contact_name;
    }
    if (editedVendor.email !== vendor.email) {
      updatePayload.email = editedVendor.email;
    }
    if (editedVendor.phone !== vendor.phone) {
      updatePayload.phone = editedVendor.phone;
    }
    if (editedVendor.website !== vendor.website) {
      updatePayload.website = editedVendor.website;
    }
    if (editedVendor.address !== vendor.address) {
      updatePayload.address = editedVendor.address;
    }
    if (editedVendor.city !== vendor.city) {
      updatePayload.city = editedVendor.city;
    }
    if (editedVendor.state !== vendor.state) {
      updatePayload.state = editedVendor.state;
    }
    if (editedVendor.zip !== vendor.zip) {
      updatePayload.zip = editedVendor.zip;
    }
    if (editedVendor.description !== vendor.description) {
      updatePayload.description = editedVendor.description;
    }
    if (editedVendor.service_types !== vendor.service_types) {
      updatePayload.service_types = editedVendor.service_types;
    }
    if (editedVendor.status !== vendor.status) {
      updatePayload.status = editedVendor.status;
    }
    if (editedVendor.tax_id !== vendor.tax_id) {
      updatePayload.tax_id = editedVendor.tax_id;
    }
    if (
      editedVendor.insurance_policy_number !== vendor.insurance_policy_number
    ) {
      updatePayload.insurance_policy_number =
        editedVendor.insurance_policy_number;
    }
    if (editedVendor.payment_terms !== vendor.payment_terms) {
      updatePayload.payment_terms = editedVendor.payment_terms;
    }
    if (editedVendor.notes !== vendor.notes) {
      updatePayload.notes = editedVendor.notes;
    }

    try {
      const response = await vendorService.updateVendor({
        vendorId: vendorId!,
        vendorUpdate: updatePayload,
      });

      setVendor(response.data);
      setIsEditing(false);
      setSuccessMessage('Vendor updated successfully!');
      setShowSuccessAlert(true);
    } catch (err) {
      console.error('Error updating vendor:', err);
      const errorMessage = apiErrorMessage(err, "Couldn't save the vendor.");

      setSaveErrorTitle("Couldn't update vendor");
      setSaveError(errorMessage);
      setShowSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!vendor) return;

    setIsDeleting(true);
    setShowSaveError(false);

    try {
      await vendorService.deleteVendor({ vendorId: vendorId! });
      navigate('/vendors');
    } catch (err) {
      console.error('Error deleting vendor:', err);
      const errorMessage = apiErrorMessage(err, "Couldn't delete the vendor.");
      setSaveErrorTitle("Couldn't delete vendor");
      setSaveError(errorMessage);
      setShowSaveError(true);
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedVendor(null);
    setFormErrors({});
    setTouchedFields({});
  };

  const handleInputChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | { target: { name: string; value: string } }
  ) => {
    if (!editedVendor) return;
    const { name, value } = e.target;

    setEditedVendor((prev) => ({ ...prev, [name]: value || null }));

    setTouchedFields((prev) => ({ ...prev, [name]: true }));

    const error = validateField(name as keyof VendorUpdate, value);
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  const handleBlur = (
    e: React.FocusEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setTouchedFields((prev) => ({ ...prev, [name]: true }));

    const error = validateField(name as keyof VendorUpdate, value);
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  const isFormValid = () => {
    if (Object.keys(formErrors).length > 0) {
      return false;
    }

    const requiredFields = ['name'];

    return requiredFields.every((field) => {
      const value = editedVendor?.[field as keyof VendorUpdate];
      if (!value) return false;
      return true;
    });
  };

  const validateField = (name: keyof VendorUpdate, value: any): string => {
    switch (name) {
      case 'name':
        return !value
          ? 'Vendor name is required'
          : value.length > 255
            ? 'Name must be less than 255 characters'
            : '';
      case 'email':
        if (!value) return '';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailRegex.test(value) ? 'Please enter a valid email' : '';
      case 'phone':
        if (!value) return '';
        return value.length > 20 ? 'Phone must be less than 20 characters' : '';
      case 'zip':
        if (!value) return '';
        return value.length > 10 ? 'ZIP must be less than 10 characters' : '';
      default:
        return '';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100';
      case 'inactive':
        return 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100';
      default:
        return 'bg-zinc-100 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-100';
    }
  };

  const formatLabel = (value: string) => {
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  // With the row in the sync store there is nothing to wait for: render
  // it on the first paint, not after the effect clears `loading` (#635).
  if (loading && !haveSynced) {
    return <LoadingSpinner></LoadingSpinner>;
  }

  // A failed load leaves nothing to show; say so rather than render an
  // empty page (#652).
  if (!vendor) {
    return (
      <SidebarLayout
        sidebar={<AppSidebar currentPage={'vendors'} />}
        navbar={<Navbar />}
      >
        <ErrorPage
          title="Couldn't load this vendor"
          message={
            fetchError ||
            'It may have been deleted, or you may not have access to it.'
          }
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage={'vendors'} />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <div className="flex justify-between items-center mb-6">
        <Heading level={2}>Vendor Details</Heading>
        <div className="flex items-center gap-2">
          {/* A work order for this vendor, in its association. Not for an
              inactive one: the work order picker leaves those out. */}
          {vendor && vendor.status !== 'inactive' && (
            <CreateActions
              scope={{ hoaId: vendor.hoa_id, vendorId: vendor.id }}
              workOrder
            />
          )}
          {canDeleteVendor() && (
            <Button color="red" onClick={() => setShowDeleteConfirm(true)}>
              Delete
            </Button>
          )}
          <Button color="sky" onClick={handleEdit}>
            Edit
          </Button>
        </div>
      </div>
      <Divider className="mt-4 mb-6" />

      <ShowAlert
        alertType="success"
        alertTitle="Success"
        alertMessage={successMessage}
        isVisible={showSuccessAlert}
        onClose={() => setShowSuccessAlert(false)}
        timeout={5000}
        isInline={true}
      />

      <ShowAlert
        alertType="error"
        alertTitle={fetchErrorTitle}
        alertMessage={fetchError}
        isVisible={showFetchError}
        onClose={() => setShowFetchError(false)}
        timeout={5000}
        isInline={true}
      />

      <ShowAlert
        alertType="error"
        alertTitle={saveErrorTitle}
        alertMessage={saveError}
        isVisible={showSaveError}
        onClose={() => setShowSaveError(false)}
        timeout={5000}
        isInline={true}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Bank details for ACH payables — its own card, its own permissions. */}
        {vendor && (
          <VendorBankDetailsCard
            hoaId={vendor.hoa_id}
            vendorId={vendor.id}
            vendorName={vendor.name}
          />
        )}
        {/* Basic Information */}
        <div className="bg-slate-100 dark:bg-gray-600 p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Basic Information
          </h3>
          <dl className="space-y-4">
            <div className="flex flex-col">
              <dt className="text-sm font-semibold dark:text-white text-gray-900 mb-1">
                Name
              </dt>
              <dd className="text-sm text-gray-900 dark:text-white">
                {vendor?.name}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm font-semibold dark:text-white text-gray-900">
                Status
              </dt>
              <dd>
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                    vendor?.status || ''
                  )}`}
                >
                  {vendor?.status ? formatLabel(vendor.status) : '-'}
                </span>
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm font-semibold dark:text-white text-gray-900">
                Service Types
              </dt>
              <dd className="text-sm text-gray-900 dark:text-white">
                {vendor?.service_types || '-'}
              </dd>
            </div>
            {vendor?.description && (
              <div className="flex flex-col">
                <dt className="text-sm font-semibold dark:text-white text-gray-900 mb-2">
                  Description
                </dt>
                <dd className="text-sm text-gray-900 dark:text-white">
                  {vendor.description}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Contact Information */}
        <div className="bg-slate-100 dark:bg-gray-600 p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Contact Information
          </h3>
          <dl className="space-y-4">
            <div className="flex justify-between items-center">
              <dt className="text-sm font-semibold dark:text-white text-gray-900">
                Contact Name
              </dt>
              <dd className="text-sm text-gray-900 dark:text-white">
                {vendor?.contact_name || '-'}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm font-semibold dark:text-white text-gray-900">
                Email
              </dt>
              <dd className="text-sm text-gray-900 dark:text-white">
                {vendor?.email ? (
                  <a
                    href={`mailto:${vendor.email}`}
                    className="text-sky-600 hover:underline"
                  >
                    {vendor.email}
                  </a>
                ) : (
                  '-'
                )}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm font-semibold dark:text-white text-gray-900">
                Phone
              </dt>
              <dd className="text-sm text-gray-900 dark:text-white">
                {vendor?.phone ? (
                  <a
                    href={`tel:${vendor.phone}`}
                    className="text-sky-600 hover:underline"
                  >
                    {vendor.phone}
                  </a>
                ) : (
                  '-'
                )}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm font-semibold dark:text-white text-gray-900">
                Website
              </dt>
              <dd className="text-sm text-gray-900 dark:text-white">
                {vendor?.website ? (
                  <a
                    href={vendor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600 hover:underline"
                  >
                    {vendor.website}
                  </a>
                ) : (
                  '-'
                )}
              </dd>
            </div>
          </dl>
        </div>

        {/* Address */}
        <div className="bg-slate-100 dark:bg-gray-600 p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Address
          </h3>
          <dl className="space-y-4">
            <div className="flex justify-between items-center">
              <dt className="text-sm font-semibold dark:text-white text-gray-900">
                Street
              </dt>
              <dd className="text-sm text-gray-900 dark:text-white">
                {vendor?.address || '-'}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm font-semibold dark:text-white text-gray-900">
                City
              </dt>
              <dd className="text-sm text-gray-900 dark:text-white">
                {vendor?.city || '-'}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm font-semibold dark:text-white text-gray-900">
                State
              </dt>
              <dd className="text-sm text-gray-900 dark:text-white">
                {vendor?.state || '-'}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm font-semibold dark:text-white text-gray-900">
                ZIP
              </dt>
              <dd className="text-sm text-gray-900 dark:text-white">
                {vendor?.zip || '-'}
              </dd>
            </div>
          </dl>
        </div>

        {/* Business Details */}
        <div className="bg-slate-100 dark:bg-gray-600 p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Business Details
          </h3>
          <dl className="space-y-4">
            <div className="flex justify-between items-center">
              <dt className="text-sm font-semibold dark:text-white text-gray-900">
                Tax ID
              </dt>
              <dd className="text-sm text-gray-900 dark:text-white">
                {vendor?.tax_id || '-'}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm font-semibold dark:text-white text-gray-900">
                Insurance Policy
              </dt>
              <dd className="text-sm text-gray-900 dark:text-white">
                {vendor?.insurance_policy_number || '-'}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm font-semibold dark:text-white text-gray-900">
                Insurance Expiry
              </dt>
              <dd className="text-sm text-gray-900 dark:text-white">
                {formatDate(vendor?.insurance_expiry)}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm font-semibold dark:text-white text-gray-900">
                Payment Terms
              </dt>
              <dd className="text-sm text-gray-900 dark:text-white">
                {vendor?.payment_terms || '-'}
              </dd>
            </div>
          </dl>
        </div>

        {/* Notes & Metadata */}
        <div className="bg-slate-100 dark:bg-gray-600 p-6 rounded-lg shadow md:col-span-2">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Additional Information
          </h3>
          <dl className="space-y-4">
            {vendor?.notes && (
              <div className="flex flex-col">
                <dt className="text-sm font-semibold dark:text-white text-gray-900 mb-2">
                  Notes
                </dt>
                <dd className="text-sm text-gray-900 dark:text-white">
                  {vendor.notes}
                </dd>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex flex-col">
                <dt className="text-sm font-semibold dark:text-white text-gray-900">
                  Created By
                </dt>
                <dd className="text-sm text-gray-900 dark:text-white">
                  {vendor?.created_by}
                </dd>
              </div>
              <div className="flex flex-col">
                <dt className="text-sm font-semibold dark:text-white text-gray-900">
                  Updated By
                </dt>
                <dd className="text-sm text-gray-900 dark:text-white">
                  {vendor?.updated_by || '-'}
                </dd>
              </div>
              <div className="flex flex-col">
                <dt className="text-sm font-semibold dark:text-white text-gray-900">
                  Created Date
                </dt>
                <dd className="text-sm text-gray-900 dark:text-white">
                  {formatDate(vendor?.date_created)}
                </dd>
              </div>
              <div className="flex flex-col">
                <dt className="text-sm font-semibold dark:text-white text-gray-900">
                  Last Updated
                </dt>
                <dd className="text-sm text-gray-900 dark:text-white">
                  {formatDate(vendor?.date_updated)}
                </dd>
              </div>
            </div>
          </dl>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog
        open={isEditing}
        onClose={() => setIsEditing(false)}
        className="w-full max-w-2xl mx-auto"
      >
        <div className="relative flex flex-col h-full">
          <div className="p-4 md:p-6">
            <DialogTitle className="text-xl font-semibold">
              Edit Vendor
            </DialogTitle>
            <button
              onClick={() => setIsEditing(false)}
              className="absolute right-4 top-4 p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-full transition-colors duration-150"
              aria-label="Close dialog"
            >
              <XMarkIcon className="h-5 w-5 text-zinc-600 dark:text-zinc-100" />
            </button>

            <Divider className="my-4" />

            <div className="mb-4">
              <ShowAlert
                alertType="error"
                alertTitle={saveErrorTitle}
                alertMessage={saveError}
                isVisible={showSaveError}
                onClose={() => setShowSaveError(false)}
                timeout={5000}
                isInline={true}
              />
            </div>

            <DialogBody>
              <Fieldset>
                <FieldGroup>
                  {/* Name and Status */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field>
                      <Label htmlFor="name">Vendor Name*</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={editedVendor?.name || ''}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter vendor name"
                        required
                        className="w-full"
                        data-invalid={touchedFields.name && formErrors.name}
                      />
                      {touchedFields.name && formErrors.name && (
                        <ErrorMessage>{formErrors.name}</ErrorMessage>
                      )}
                    </Field>

                    <Field>
                      <Label>Status</Label>
                      <FieldPicker
                        label="Status"
                        options={statusOptions}
                        value={editedVendor?.status}
                        onChange={(value) =>
                          handleInputChange({
                            target: { name: 'status', value },
                          })
                        }
                        placeholder="Select Status"
                      />
                    </Field>
                  </div>

                  {/* Contact Name and Service Types */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field>
                      <Label htmlFor="contact_name">Contact Name</Label>
                      <Input
                        id="contact_name"
                        name="contact_name"
                        type="text"
                        value={editedVendor?.contact_name || ''}
                        onChange={handleInputChange}
                        placeholder="Enter contact name"
                        className="w-full"
                      />
                    </Field>

                    <Field>
                      <Label htmlFor="service_types">Service Types</Label>
                      <Input
                        id="service_types"
                        name="service_types"
                        type="text"
                        value={editedVendor?.service_types || ''}
                        onChange={handleInputChange}
                        placeholder="e.g., Landscaping, Plumbing"
                        className="w-full"
                      />
                    </Field>
                  </div>

                  {/* Email and Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={editedVendor?.email || ''}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter email"
                        className="w-full"
                        data-invalid={touchedFields.email && formErrors.email}
                      />
                      {touchedFields.email && formErrors.email && (
                        <ErrorMessage>{formErrors.email}</ErrorMessage>
                      )}
                    </Field>

                    <Field>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={editedVendor?.phone || ''}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter phone number"
                        className="w-full"
                        data-invalid={touchedFields.phone && formErrors.phone}
                      />
                      {touchedFields.phone && formErrors.phone && (
                        <ErrorMessage>{formErrors.phone}</ErrorMessage>
                      )}
                    </Field>
                  </div>

                  {/* Website */}
                  <Field>
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      name="website"
                      type="url"
                      value={editedVendor?.website || ''}
                      onChange={handleInputChange}
                      placeholder="https://example.com"
                      className="w-full"
                    />
                  </Field>

                  {/* Address */}
                  <Field>
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      name="address"
                      type="text"
                      value={editedVendor?.address || ''}
                      onChange={handleInputChange}
                      placeholder="Enter street address"
                      className="w-full"
                    />
                  </Field>

                  {/* City, State, ZIP */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Field>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        name="city"
                        type="text"
                        value={editedVendor?.city || ''}
                        onChange={handleInputChange}
                        placeholder="City"
                        className="w-full"
                      />
                    </Field>

                    <Field>
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        name="state"
                        type="text"
                        value={editedVendor?.state || ''}
                        onChange={handleInputChange}
                        placeholder="State"
                        className="w-full"
                      />
                    </Field>

                    <Field>
                      <Label htmlFor="zip">ZIP</Label>
                      <Input
                        id="zip"
                        name="zip"
                        type="text"
                        value={editedVendor?.zip || ''}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="ZIP"
                        className="w-full"
                        data-invalid={touchedFields.zip && formErrors.zip}
                      />
                      {touchedFields.zip && formErrors.zip && (
                        <ErrorMessage>{formErrors.zip}</ErrorMessage>
                      )}
                    </Field>
                  </div>

                  {/* Business Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field>
                      <Label htmlFor="tax_id">Tax ID</Label>
                      <Input
                        id="tax_id"
                        name="tax_id"
                        type="text"
                        value={editedVendor?.tax_id || ''}
                        onChange={handleInputChange}
                        placeholder="Enter tax ID"
                        className="w-full"
                      />
                    </Field>

                    <Field>
                      <Label htmlFor="payment_terms">Payment Terms</Label>
                      <Input
                        id="payment_terms"
                        name="payment_terms"
                        type="text"
                        value={editedVendor?.payment_terms || ''}
                        onChange={handleInputChange}
                        placeholder="e.g., Net 30"
                        className="w-full"
                      />
                    </Field>
                  </div>

                  {/* Insurance */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field>
                      <Label htmlFor="insurance_policy_number">
                        Insurance Policy Number
                      </Label>
                      <Input
                        id="insurance_policy_number"
                        name="insurance_policy_number"
                        type="text"
                        value={editedVendor?.insurance_policy_number || ''}
                        onChange={handleInputChange}
                        placeholder="Enter policy number"
                        className="w-full"
                      />
                    </Field>

                    <Field>
                      <Label htmlFor="insurance_expiry">Insurance Expiry</Label>
                      <Input
                        id="insurance_expiry"
                        name="insurance_expiry"
                        type="date"
                        value={editedVendor?.insurance_expiry || ''}
                        onChange={handleInputChange}
                        className="w-full"
                      />
                    </Field>
                  </div>

                  {/* Description */}
                  <Field>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={editedVendor?.description ?? ''}
                      onChange={handleInputChange}
                      placeholder="Describe the vendor's services"
                      className="w-full"
                      rows={2}
                    />
                  </Field>

                  {/* Notes */}
                  <Field>
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={editedVendor?.notes ?? ''}
                      onChange={handleInputChange}
                      placeholder="Additional notes"
                      className="w-full"
                      rows={2}
                    />
                  </Field>
                </FieldGroup>
              </Fieldset>
            </DialogBody>
          </div>

          <div className="sticky bottom-0 mt-auto border-t border-gray-200 dark:border-gray-700 bg-stone-100 dark:bg-gray-800 p-4 md:p-6">
            <div className="flex flex-col space-y-3">
              <Button
                onClick={handleSave}
                color="sky"
                disabled={isSaving || !isFormValid()}
                className="w-full disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                onClick={handleCancel}
                color="zinc"
                disabled={isSaving}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        className="w-full max-w-md mx-auto"
      >
        <div className="p-6">
          <DialogTitle className="text-xl font-semibold text-red-600">
            Delete Vendor
          </DialogTitle>
          <Divider className="my-4" />
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            Are you sure you want to delete <strong>{vendor?.name}</strong>?
            This action cannot be undone.
          </p>
          <div className="flex flex-col space-y-3">
            <Button
              onClick={handleDelete}
              color="red"
              disabled={isDeleting}
              className="w-full"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
            <Button
              onClick={() => setShowDeleteConfirm(false)}
              color="zinc"
              disabled={isDeleting}
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Dialog>
    </SidebarLayout>
  );
};

export default VendorDetailPage;
